import { Component, OnInit, ViewChild } from "@angular/core";
import { FlightsService } from "../../flights-calendar/flights.service";
import moment from "moment";
import { FormBuilder, FormGroup } from "@angular/forms";
import { combineLatest } from "rxjs";
import { LocalStorageService } from "../../shared/local-storage.service";
import { Helicopter } from "../../shared/models/helicopter.model";
import { MatTable } from "@angular/material/table";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { AppConfigService } from "../../app-config/app-config.service";
import { AppConfigType } from "../../shared/models/appConfig.model";

@Component({
    selector: "app-flights-table",
    templateUrl: "./flights-table.component.html",
    styleUrls: ["./flights-table.component.scss"],
    standalone: false,
})
export class FlightsTableComponent implements OnInit {
    @ViewChild("flightsTable", { static: false }) flightsTable: MatTable<any>;

    public flightsFilterForm: FormGroup;
    public displayedColumns: string[] = [
        "flightType",
        "flightDate",
        "duration",
        "departurePlace",
        "arrivalPlace",
        "pilot",
        "helicopter",
        "passengers",
        "crs",
        "refuel",
        "firstRefuelLocation",
        "refuelBeforeFirstFlight",
        "secondRefuelLocation",
        "actions",
    ];
    public dataSource = [];
    public helicopterOptions: Helicopter[] = [];
    public hasRefuelLocations = false;
    public hasRefuelLocationsPrefill = false;
    public refuelLocationFlights = [];

    public isAdmin = false;

    private _userSessionObject;

    constructor(
        private _flightsService: FlightsService,
        private _formBuilder: FormBuilder,
        private _localStorageService: LocalStorageService,
        private _snackBar: MatSnackBar,
        private _translate: TranslateService,
        private _appConfigService: AppConfigService,
    ) {
        this._userSessionObject = this._localStorageService.getUserLocalStorageObject();
        this.flightsFilterForm = this._formBuilder.group({
            startDate: [moment().clone().startOf("month")],
            endDate: [moment().clone().endOf("month")],
            helicopterId: [],
        });
    }

    ngOnInit(): void {
        const startDate = moment(this.flightsFilterForm.value.startDate).format("YYYY-MM-DD");
        const endDate = moment(this.flightsFilterForm.value.endDate).format("YYYY-MM-DD");
        if (this._userSessionObject.htlAccountTypes.length > 0) {
            switch (this._userSessionObject.htlAccountTypes[0].id) {
                case 1:
                    this.isAdmin = true;
                    break;
                case 2:
                    this.isAdmin = true;
                    break;
                case 3:
                    this.isAdmin = true;
                    break;
            }
        }
        combineLatest([
            this._flightsService.getPilotesByOrgId(this._userSessionObject.organizationId.toString()),
            this._flightsService.getHelicoptersByOrgId(this._userSessionObject.organizationId.toString()),
            this._flightsService.getHelicopterFlights(startDate, endDate, null),
            this._appConfigService.get(),
        ]).subscribe((result) => {
            this.helicopterOptions = result[1];
            this.dataSource = result[2].slice().reverse();
            const flightRefuelLocationConfig = result[3].find((config) => config.type === AppConfigType.flightRefuelLocation);
            this.hasRefuelLocations = JSON.parse(flightRefuelLocationConfig.value)["camo"];
            this.hasRefuelLocationsPrefill = flightRefuelLocationConfig ? JSON.parse(flightRefuelLocationConfig.value)["prefillNextFlights"] : false;
        });

        this.flightsFilterForm.valueChanges.subscribe((values) => {
            this._flightsService
                .getHelicopterFlights(moment(values.startDate).format("YYYY-MM-DD"), moment(values.endDate).format("YYYY-MM-DD"), values.helicopterId)
                .subscribe((result) => {
                    this.dataSource = result.slice().reverse();
                    this.flightsTable.renderRows();
                });
        });
    }

    getTotalPassengers() {
        return this.dataSource.map((t) => t.passengers).reduce((acc, value) => acc + value, 0);
    }

    downloadExcelMassAndBalance(flightId: number) {
        this._flightsService.getMassBalanceByFlightId(flightId).subscribe(
            (result) => {
                console.log(result);
                window.open(result.downloadLink, "_blank");
            },
            (error) => {
                // if (error.status === 655) {
                this._snackBar.open(this._translate.instant("FLIGHTS.TABLE.noAvailableMassBalanceFile"), this._translate.instant("GENERAL.close"), {
                    duration: 3000,
                });
                // }
            },
        );
    }

    saveFlightLocations() {
        this._flightsService.updateFlightLocations(this.refuelLocationFlights).subscribe(
            (result) => {
                if (this.hasRefuelLocationsPrefill) {
                    result.forEach((updatedFlight) => {
                        const flightIndex = this.dataSource.findIndex((f) => f.id === updatedFlight.id);
                        if (flightIndex !== -1) {
                            this.dataSource[flightIndex].firstRefuelLocation = updatedFlight.firstRefuelLocation;
                            this.dataSource[flightIndex].secondRefuelLocation = updatedFlight.secondRefuelLocation;
                            if (this.dataSource[flightIndex].successiveFlights) {
                                this.dataSource[flightIndex].successiveFlights.forEach((successiveFlight) => {
                                    const successiveFlightIndex = this.dataSource.findIndex((sf) => sf.id === successiveFlight.id);
                                    if (successiveFlightIndex !== -1) {
                                        this.dataSource[successiveFlightIndex].firstRefuelLocation = successiveFlight.firstRefuelLocation;
                                        this.dataSource[successiveFlightIndex].secondRefuelLocation = successiveFlight.secondRefuelLocation;
                                    }
                                });
                            }
                            this.dataSource[flightIndex].successiveFlights = [];
                        }
                    });
                }
                this._snackBar.open("Refuel locations updated successfully", "Close", {
                    duration: 3000,
                });
            },
            (error) => {
                this._snackBar.open("There was an error updating the refuel locations", "Close", {
                    duration: 3000,
                });
            },
        );
    }

    onRefuelLocationChange(flight) {
        const existingFlight = this.refuelLocationFlights.find((f) => f.id === flight.id);
        if (existingFlight) {
            existingFlight.firstRefuelLocation = flight.firstRefuelLocation;
            existingFlight.secondRefuelLocation = flight.secondRefuelLocation;
            if (this.hasRefuelLocationsPrefill) {
                existingFlight.successiveFlights.forEach((successiveFlight) => {
                    successiveFlight.firstRefuelLocation = flight.firstRefuelLocation;
                    if (successiveFlight.firstRefuelLocation && flight.secondRefuelLocation) {
                        successiveFlight.firstRefuelLocation += " ";
                    }
                    successiveFlight.firstRefuelLocation += flight.secondRefuelLocation;
                });
            }
        } else {
            if (this.hasRefuelLocationsPrefill) {
                flight.successiveFlights = [];
                const flightsReversed = this.dataSource.slice().reverse();
                const flightIndex = flightsReversed.findIndex((f) => f.id === flight.id);
                for (let i = flightIndex + 1; i < flightsReversed.length; i++) {
                    const successiveFlight = structuredClone(flightsReversed[i]);
                    if (successiveFlight.firstRefuelLocation || successiveFlight.secondRefuelLocation) {
                        break;
                    }
                    successiveFlight.firstRefuelLocation = flight.firstRefuelLocation;
                    if (successiveFlight.firstRefuelLocation && flight.secondRefuelLocation) {
                        successiveFlight.firstRefuelLocation += " ";
                    }
                    successiveFlight.firstRefuelLocation += flight.secondRefuelLocation;
                    flight.successiveFlights.push(successiveFlight);
                }
            }
            this.refuelLocationFlights.push(flight);
        }
    }
}
